import {
  ApolloClient,
  NormalizedCacheObject,
  QueryOptions,
  OperationVariables,
} from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { GetAsyncJobsDocument } from "~/graphql/supergraphHooks";
import { type GetAsyncJobsQuery, type JobType } from "~/graphql/supergraphTypes";

export interface ParsedJobData {
  errors?: string[];
  s3_key?: string;
  total_rows?: number;
  failed_rows?: number;
  success_rate?: string;
  output_s3_key?: string | null;
  successful_rows?: number;
  total_fields_processed?: number;
}

export interface BulkUploadRowData {
  id: string;
  status: string;
  createdAt: string;
  createdBy: string;
  data: ParsedJobData | null;
}

const parseJobData = (raw: unknown): ParsedJobData | null => {
  try {
    const data: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
    return typeof data === "object" && data !== null ? data : null;
  } catch {
    return null;
  }
};

export const rowsFromQuery = (
  asyncJobs: GetAsyncJobsQuery["asyncJobs"],
): BulkUploadRowData[] => {
  if (!asyncJobs?.edges?.length) {
    return [];
  }

  return asyncJobs.edges.map((edge) => {
    const node = edge.node;
    const createdBy = node.createdBy
      ? [
          (node.createdBy.firstName ?? "")?.trim(),
          (node.createdBy.middleName ?? "")?.trim(),
          (node.createdBy.lastName ?? "")?.trim(),
        ]
          .filter(Boolean)
          .join(" ")
      : "-";
    return {
      id: node.id,
      status: node.status,
      createdAt: String(node.createdAt),
      createdBy,
      data: parseJobData(node.data),
    };
  });
};

export class BulkUploadGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  asyncJobType: JobType;
  private cursors: Map<number, string> = new Map();

  constructor(
    asyncJobType: JobType,
    apolloClient: ApolloClient<NormalizedCacheObject> = client,
  ) {
    this.client = apolloClient;
    this.asyncJobType = asyncJobType;
  }

  public handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  public handleSuccess(
    params: AgGrid.IServerSideGetRowsParams,
    response: GetAsyncJobsQuery,
  ): void {
    if (!response.asyncJobs) {
      this.handleError(params);
      return;
    }

    const rowData = rowsFromQuery(response.asyncJobs);
    const totalCount = response.asyncJobs.count || 0;
    const pageInfo = response.asyncJobs.pageInfo;

    if (pageInfo?.endCursor) {
      const endRow = params.request.endRow ?? 0;
      const nextPageStart = endRow;
      this.cursors.set(nextPageStart, pageInfo.endCursor);
    }

    params.success({
      rowData,
      rowCount: totalCount,
    });

    if (totalCount === 0) {
      params.api?.showNoRowsOverlay();
    } else {
      params.api?.hideOverlay();
    }
  }

  public buildQueryOptions(
    params: AgGrid.IServerSideGetRowsParams,
  ): QueryOptions<OperationVariables, GetAsyncJobsQuery> {
    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? 0;
    const first = endRow - startRow;

    const after =
      startRow > 0 && this.cursors.has(startRow) ? this.cursors.get(startRow) : null;

    return {
      query: GetAsyncJobsDocument,
      variables: {
        filters: {
          jobTypes: [this.asyncJobType],
        },
        first: first > 0 ? first : 25,
        after,
        sort: ["CREATED_AT_DESC"],
      },

      fetchPolicy: "no-cache",
    };
  }

  public executeQuery(
    params: AgGrid.IServerSideGetRowsParams,
    queryOptions: ReturnType<typeof this.buildQueryOptions>,
  ): Promise<void> {
    return this.client
      .query<GetAsyncJobsQuery>(queryOptions)
      .then((response) => {
        this.handleSuccess(params, response.data);
      })
      .catch((_error) => {
        this.handleError(params);
      });
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const queryOptions = this.buildQueryOptions(params);
    void this.executeQuery(params, queryOptions);
  }
}
